import {
  Card,
  Typography,
  Chip,
  Switch,
  Tooltip,
  Divider,
  Box,
  FormControlLabel,
  Paper,
  alpha,
  IconButton,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactMultiEmail } from 'react-multi-email';
import 'react-multi-email/style.css';
import { styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import client from 'client';

const StyledCard = styled(Card)(({ theme, enabled }) => {
  const yellowColor = theme.palette.warning?.main || '#ffc107';
  const yellowLight = theme.palette.warning?.light || '#ffecb3';

  return {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2.5),
    background: enabled
      ? `linear-gradient(135deg, ${alpha(yellowColor, 0.08)} 0%, ${alpha(
          yellowLight,
          0.05
        )} 100%)`
      : theme.palette.background.paper,
    border: `1px solid ${
      enabled ? alpha(yellowColor, 0.3) : theme.palette.divider
    }`,
    boxShadow: enabled
      ? `0 4px 20px ${alpha(yellowColor, 0.15)}`
      : theme.shadows[2],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'visible',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: enabled
        ? `0 8px 30px ${alpha(yellowColor, 0.25)}`
        : theme.shadows[8],
      borderColor: enabled ? alpha(yellowColor, 0.4) : theme.palette.divider,
      background: enabled
        ? `linear-gradient(135deg, ${alpha(yellowColor, 0.12)} 0%, ${alpha(
            yellowLight,
            0.08
          )} 100%)`
        : theme.palette.background.paper,
    },
    '&::before': enabled
      ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${yellowColor}, ${yellowLight})`,
          borderRadius: `${theme.spacing(2.5)} ${theme.spacing(2.5)} 0 0`,
        }
      : {},
  };
});

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  '& svg': {
    color: theme.palette.primary.main,
    fontSize: 24,
  },
}));

const StatusBadge = styled(Box)(({ theme, enabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(3),
  fontSize: '0.75rem',
  fontWeight: 600,
  background: enabled
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.grey[500], 0.1),
  color: enabled ? theme.palette.success.main : theme.palette.text.secondary,
}));

const EmailInputContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  minHeight: 80,
  '& .react-multi-email': {
    margin: 0,
    '& input': {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      fontSize: '0.875rem',
      padding: theme.spacing(0.5),
      '&::placeholder': {
        color: theme.palette.text.disabled,
      },
      '&:focus': {
        outline: 'none',
      },
    },
    '& li': {
      margin: theme.spacing(0.5),
    },
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 32,
  borderRadius: theme.spacing(1),
  fontWeight: 500,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
  },
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.error.dark,
    },
  },
}));

const SwitchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  background: alpha(theme.palette.background.default, 0.5),
  border: `1px solid ${theme.palette.divider}`,
}));

const TemplateSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.info.main,
    0.05
  )} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
  borderLeft: `4px solid ${theme.palette.info.main}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const ExplanationBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  background: alpha(theme.palette.background.paper, 0.6),
  marginTop: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,
    '& + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[400],
  },
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.default,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  position: 'relative',
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const PreviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '& .MuiTypography-subtitle2': {
    color: theme.palette.primary.main,
  },
}));

const PreviewIframeContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxHeight: '300px',
  borderRadius: theme.spacing(1),
  overflow: 'auto',
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  background: theme.palette.background.paper,
  position: 'relative',
  boxShadow: `inset 0 2px 4px ${alpha(theme.palette.common.black, 0.06)}`,
  '& iframe': {
    width: '100%',
    minHeight: '600px',
    border: 'none',
    display: 'block',
    transform: 'scale(0.5)',
    transformOrigin: 'top left',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.divider, 0.1),
    borderRadius: theme.spacing(0.5),
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.3),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

const PreviewButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const EmailConfigEntry = ({
  emailGroup,
  email_name,
  index,
  onChange,
  onRemove,
  changeEnabled,
  changeSendIfEmpty,
}) => {
  const { t } = useTranslation('navigation');
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');

  const nameGenerator = (key) => {
    let name = key.replaceAll('_', ' ');
    let name_split = name.split(' ');
    let name_final = '';
    name_split.forEach((word) => {
      name_final += word.charAt(0).toUpperCase() + word.slice(1) + ' ';
    });
    return name_final.trim();
  };

  const getPreviewDescription = (type) => {
    const descriptions = {
      ticket_emails: t('TICKET_EMAILS_DESCRIPTION'),
      invoice_emails: t('INVOICE_EMAILS_DESCRIPTION'),
      job_emails: t('JOB_EMAILS_DESCRIPTION'),
      inspection_emails: t('INSPECTION_EMAILS_DESCRIPTION'),
      server_restart_success: t('SERVER_RESTART_SUCCESS_DESCRIPTION'),
      server_restart_failure: t('SERVER_RESTART_FAILURE_DESCRIPTION'),
      subscription_past_due:
        'Sent on configured days after a subscription charge fails. Includes days remaining in the 11-day grace period.',
    };
    return (
      descriptions[type] ||
      `${t('EMAIL_CONFIGURATION_DEFAULT_DESCRIPTION')} ${nameGenerator(
        type
      ).toLowerCase()}`
    );
  };

  const isEnabled = emailGroup.enabled || false;
  const emailCount = emailGroup.data?.length || 0;

  // Cargar el preview automáticamente cuando el componente se monta
  useEffect(() => {
    const loadPreview = async () => {
      if (!email_name) return;

      try {
        setPreviewLoading(true);
        const result = await client.post('/props/preview-email-template', {
          email_type: email_name,
        });
        if (result.data.status) {
          setPreviewHtml(result.data.html);
          setPreviewSubject(result.data.subject);
        } else {
          setPreviewHtml(`<p>${t('ERROR_LOADING_PREVIEW')}</p>`);
          setPreviewSubject('Error');
        }
      } catch (error) {
        console.error('Error loading preview:', error);
        setPreviewHtml(`<p>${t('ERROR_LOADING_PREVIEW')}</p>`);
        setPreviewSubject('Error');
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [email_name]);

  const handlePreview = () => {
    // Solo toggle del estado expandido/colapsado
    setPreviewExpanded(!previewExpanded);
  };

  return (
    <StyledCard enabled={isEnabled}>
      <HeaderSection>
        <TitleRow>
          <Box display='flex' alignItems='center' flex={1}>
            {/* <IconWrapper>
              <EmailIcon />
            </IconWrapper> */}
            <Box flex={1}>
              <Typography
                variant='h6'
                fontWeight={700}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(
                    '#000',
                    0.8
                  )} 0%, ${alpha('#000', 0.6)} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {email_name
                  ? nameGenerator(email_name)
                  : t('GROUP_NAME_MISSING')}
              </Typography>
              <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                <Typography variant='caption' color='text.secondary'>
                  {emailCount}{' '}
                  {emailCount === 1
                    ? t('EMAIL_COUNT_SINGULAR')
                    : t('EMAIL_COUNT_PLURAL')}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  •
                </Typography>
                <StatusBadge enabled={isEnabled}>
                  {isEnabled ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                      {t('ACTIVE_STATUS')}
                    </>
                  ) : (
                    <>
                      <CancelIcon sx={{ fontSize: 14 }} />
                      {t('INACTIVE_STATUS')}
                    </>
                  )}
                </StatusBadge>
              </Box>
            </Box>
          </Box>
          <Tooltip
            title={getPreviewDescription(email_name)}
            arrow
            placement='top'
          >
            <InfoIcon
              sx={{
                fontSize: 20,
                color: 'text.secondary',
                cursor: 'help',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            />
          </Tooltip>
        </TitleRow>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            fontStyle: 'italic',
            lineHeight: 1.6,
            mt: 1,
          }}
        >
          {getPreviewDescription(email_name)}
        </Typography>
      </HeaderSection>

      <Divider sx={{ my: 1.5 }} />

      <Collapse in={previewExpanded}>
        <PreviewContainer>
          <PreviewHeader>
            <Box display='flex' alignItems='center' gap={1}>
              <VisibilityIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant='subtitle2' fontWeight={600}>
                {t('EMAIL_PREVIEW')}
              </Typography>
            </Box>
            {/* <Tooltip
              title={
                previewExpanded
                  ? 'Ocultar vista previa'
                  : 'Ver vista previa del correo electrónico'
              }
              arrow
            >
              <PreviewButton
                size='small'
                onClick={handlePreview}
                disabled={previewLoading}
                sx={{
                  color: previewExpanded ? 'primary.main' : 'text.secondary',
                }}
              >
                {previewLoading ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <ExpandLessIcon />
                )}
              </PreviewButton>
            </Tooltip> */}
          </PreviewHeader>
          {previewLoading ? (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight='200px'
            >
              <CircularProgress size={32} />
              <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                {t('LOADING_PREVIEW')}
              </Typography>
            </Box>
          ) : previewHtml ? (
            <PreviewIframeContainer>
              <iframe
                title='Email Preview'
                srcDoc={previewHtml}
                style={{
                  width: '200%',
                  height: '600px',
                  border: 'none',
                }}
              />
            </PreviewIframeContainer>
          ) : null}
        </PreviewContainer>
      </Collapse>

      {!previewExpanded && (
        <Box mb={2.5}>
          <Tooltip title={t('VIEW_EMAIL_PREVIEW')} arrow>
            <PreviewButton
              size='small'
              onClick={handlePreview}
              disabled={previewLoading}
              sx={{
                color: 'text.secondary',
                mb: 1.5,
              }}
            >
              {previewLoading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <VisibilityIcon sx={{ fontSize: 20 }} />
              )}
            </PreviewButton>
          </Tooltip>
        </Box>
      )}

      <Box mb={2.5}>
        <Typography
          variant='subtitle2'
          fontWeight={600}
          color='text.primary'
          mb={1.5}
        >
          {t('EMAIL_ADDRESSES')}
        </Typography>
        <EmailInputContainer elevation={0}>
          <ReactMultiEmail
            placeholder={
              t('INPUT_YOUR_EMAIL_ADDRESS') ||
              'Ingresa direcciones de correo...'
            }
            emails={emailGroup.data || []}
            onChange={(_emails) => {
              console.log(_emails);
              onChange(index, _emails);
            }}
            getLabel={(email, index, removeEmail) => {
              return (
                <StyledChip
                  data-tag-handle
                  label={email}
                  onDelete={() => onRemove(index, email)}
                  deleteIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                />
              );
            }}
            style={{
              width: '100%',
            }}
          />
        </EmailInputContainer>
      </Box>

      <SwitchContainer>
        <Box>
          <Box display='flex' alignItems='center' gap={1} mb={1.5}>
            <Typography
              variant='subtitle2'
              fontWeight={700}
              color='text.primary'
            >
              {t('NOTIFICATION_STATUS')}
            </Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant='body2' fontWeight={600} gutterBottom>
                    {t('WHAT_DOES_ENABLED_MEAN')}
                  </Typography>
                  <Typography variant='body2'>
                    {t('ENABLED_DESCRIPTION')}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ mt: 1, fontStyle: 'italic' }}
                  >
                    {t('DISABLED_DESCRIPTION')}
                  </Typography>
                </Box>
              }
              arrow
              placement='top'
            >
              <InfoIcon
                sx={{
                  fontSize: 16,
                  color: 'primary.main',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
          <StyledFormControlLabel
            control={
              <StyledSwitch
                checked={isEnabled}
                onChange={(e) => {
                  changeEnabled(index, e.target.checked);
                }}
                size='medium'
              />
            }
            label={
              <Box>
                <Typography variant='body2' fontWeight={600}>
                  {t('ENABLED') || 'Habilitado'}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}
                >
                  {t('ENABLED_TOGGLE_DESCRIPTION')}
                </Typography>
              </Box>
            }
          />
        </Box>
      </SwitchContainer>

      <TemplateSection>
        <SectionTitle variant='subtitle2'>
          <DescriptionIcon sx={{ fontSize: 18, color: 'info.main' }} />
          {t('TEMPLATE_GENERAL_CONFIG')}
        </SectionTitle>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 2, lineHeight: 1.6 }}
        >
          {t('TEMPLATE_GENERAL_CONFIG_DESC')}
        </Typography>
        <StyledFormControlLabel
          control={
            <StyledSwitch
              checked={emailGroup.send_if_empty || false}
              onChange={(e) => {
                changeSendIfEmpty(index, e.target.checked);
              }}
              size='medium'
            />
          }
          label={
            <Box>
              <Box display='flex' alignItems='center' gap={1}>
                <Typography variant='body2' fontWeight={600}>
                  {t('SEND_IF_NO_DATA') || 'Enviar si no hay datos'}
                </Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant='body2' fontWeight={600} gutterBottom>
                        {t('WHAT_DOES_SEND_IF_NO_DATA_MEAN')}
                      </Typography>
                      <Typography variant='body2'>
                        {t('SEND_IF_NO_DATA_DESCRIPTION')}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        {t('SEND_IF_NO_DATA_DISABLED_DESC')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement='top'
                >
                  <InfoIcon
                    sx={{
                      fontSize: 14,
                      color: 'info.main',
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}
              >
                {t('SEND_IF_NO_DATA_TOGGLE_DESC')}
              </Typography>
            </Box>
          }
        />
      </TemplateSection>
    </StyledCard>
  );
};

export default EmailConfigEntry;
